import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosResponse,
} from 'axios';
import { baseService } from './index';
import {
  commonErrorformatter,
  generateQueryParams,
  getAllStoreProductsEP,
  getPreviewPageEP,
  getTemplate,
  IQueryParams,
} from './constant';
import {
  StartCheckoutPayload,
  CheckoutDataPayload,
  GetPreCheckoutUpSellPayload,
  GetPreCheckoutUpSellResponse,
  GetPostCheckoutUpSellPayload,
  CheckoutUpdateItemsPayload,
  CheckoutCompletePayload,
  CheckoutCompleteResponse,
  AddItemPayload,
  SetOrderDetailsResponse,
  SetContactInfoPayload,
  SetShippingAddressPayload,
  PageDataResponse,
  SetShippingMethodPayload,
  IPixelResponse,
  GetPageData,
  IDoVGSDecryptForGPay,
  GetPostCheckoutUpSellResponse,
} from '../interface/apiServiceInterface';
import {
  ApplyDiscountCodePayload,
  IApplePayLog,
  ICreateFunnelishConfig,
  IDoMerchantValidation,
  ISaveFunnelishProducts,
  ISwapCartProduct,
} from '../interface/checkoutInterface';
import { postUpsellPurchasePayload } from '../interface/postUpsellInterface';
import {
  ICountryResponse,
  Shop,
} from '../../page-builder/interface/storeInterface';
import {
  getIsStaging,
  getIsViewIsEdit,
  MOCK_CREDIT_CARD_NUMBERS,
  USE_MOCK_API,
} from '../utils/constants';
import { MockCheckoutService } from './mockCheckoutService';
import { OrderAndUpSellSummary } from '../interface/commonInterface';
import { ICheckoutPage } from '../interface/pageInterface';
import { cfBaseService } from './cfBaseService';
import { NewBaseService } from './newBaseService';
import { getMetaPayload } from '../utils/conversionAPI';
import { decompressData } from '../utils/gzip';

class CheckoutService {
  private useMockApi: boolean;

  constructor() {
    this.useMockApi = false;
    this.initializeMockApi();
  }

  initializeMockApi() {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const IsMockAPIEnnable = params.get(USE_MOCK_API);
      this.useMockApi = IsMockAPIEnnable === 'true';
    }
  }

  setContactInfo = async (payload: SetContactInfoPayload) => {
    try {
      const response = await NewBaseService.post<SetOrderDetailsResponse>(
        '/checkout/set_contact_info',
        payload,
      );
      return response;
    } catch (error) {
      // here we can do the common error handling for this API
      throw commonErrorformatter(error as AxiosError);
    }
  };
  setShippingAddress = async (
    payload: SetShippingAddressPayload,
    uniqueEventId: string,
  ) => {
    const newPayload = {
      ...payload,
      ...getMetaPayload(uniqueEventId),
    };
    try {
      const response = await NewBaseService.post<SetOrderDetailsResponse>(
        '/checkout/set_shipping_address',
        newPayload,
      );
      return response;
    } catch (error) {
      throw commonErrorformatter(error as AxiosError);
    }
  };
  getPixel = async (payload: { shopId: string }) => {
    try {
      const response = await NewBaseService.post<IPixelResponse>(
        '/checkout/pixel',
        payload,
      );
      return response;
    } catch (error) {
      throw commonErrorformatter(error);
    }
  };
  checkoutStart = async (payload: StartCheckoutPayload) => {
    try {
      const response = await NewBaseService.post<CheckoutDataPayload>(
        '/checkout/start',
        payload,
      );
      return response;
    } catch (error) {
      throw commonErrorformatter(error as AxiosError);
    }
  };
  checkoutComplete = async (
    payload: CheckoutCompletePayload,
    uniqueId: string,
  ) => {
    try {
      const isMockCreditCard = MOCK_CREDIT_CARD_NUMBERS.includes(
        payload?.CardInfo?.Number || '',
      );

      if (this.useMockApi && isMockCreditCard) {
        const mockResponse: AxiosResponse<CheckoutCompleteResponse> = {
          data: {
            PurchaseToken: '1234567890',
            OrderID: '1234567890',
            PurchaseTokenExpiration: '2024-01-01T00:00:00Z',
          },
          status: 200,
          statusText: 'OK',
          headers: {
            'x-phx-request-id': 'mock-request-id',
          },
          config: {
            headers: new AxiosHeaders({
              'x-phx-request-id': 'mock-request-id',
            }),
          },
        };

        return Promise.resolve(mockResponse);
      }

      const newPayload = {
        ...payload,
        ...getMetaPayload(uniqueId),
      };

      const response = await cfBaseService.post<CheckoutCompleteResponse>(
        '/complete',
        newPayload,
      );
      return response;
    } catch (error) {
      throw commonErrorformatter(error as AxiosError);
    }
  };
  getPreCheckoutUpSells = async (payload: GetPreCheckoutUpSellPayload) => {
    try {
      const response = await NewBaseService.post<GetPreCheckoutUpSellResponse>(
        '/checkout/upsells/pre_checkout',
        payload,
      );
      return response;
    } catch (error) {
      console.error('Error in getPreCheckoutUpSells funtion:', error);
      throw commonErrorformatter(error as AxiosError);
    }
  };
  getPostCheckoutUpSells = async (payload: GetPostCheckoutUpSellPayload) => {
    try {
      const response = await NewBaseService.post<GetPostCheckoutUpSellResponse>(
        '/checkout/upsells/post_checkout',
        payload,
      );
      return response;
    } catch (error) {
      console.error('Error in getPostCheckoutUpSells funtion:', error);
      throw commonErrorformatter(error as AxiosError);
    }
  };

  getPostCheckoutUpSellsV2 = async (payload: GetPostCheckoutUpSellPayload) => {
    try {
      const response = await NewBaseService.post(
        '/checkout/upsells/post_checkout_v2',
        payload,
      );
      return response;
    } catch (error) {
      console.error('Error in getPostCheckoutUpSells funtion:', error);
      throw commonErrorformatter(error as AxiosError);
    }
  };
  checkoutUpdateItems = async (payload: CheckoutUpdateItemsPayload) => {
    try {
      const response = await NewBaseService.post<CheckoutDataPayload>(
        '/checkout/update_items',
        payload,
      );
      return response;
    } catch (error) {
      throw commonErrorformatter(error as AxiosError);
    }
  };
  checkoutAddItem = async (payload: AddItemPayload) => {
    try {
      const response = await NewBaseService.post<CheckoutDataPayload>(
        '/checkout/add_items',
        payload,
      );
      return response;
    } catch (error) {
      throw commonErrorformatter(error as AxiosError);
    }
  };

  postUpsellPurchase = async (payload: postUpsellPurchasePayload) => {
    try {
      if (this.useMockApi) {
        const mockResponse: AxiosResponse<CheckoutCompleteResponse | null> = {
          data: null,
          status: 200,
          statusText: 'OK',
          headers: {
            'x-phx-request-id': 'mock-request-id',
          },
          config: {
            headers: new AxiosHeaders({
              'x-phx-request-id': 'mock-request-id',
            }),
          },
        };
        return Promise.resolve(mockResponse);
      }
      const response = await NewBaseService.post(
        '/checkout/upsell/complete',
        payload,
      );
      return response;
    } catch (error) {
      throw commonErrorformatter(error as AxiosError);
    }
  };
  CreateUpsellCart = async (payload: postUpsellPurchasePayload) => {
    try {
      if (this.useMockApi) {
        const mockResponse: AxiosResponse<CheckoutCompleteResponse | null> = {
          data: null,
          status: 200,
          statusText: 'OK',
          headers: {
            'x-phx-request-id': 'mock-request-id',
          },
          config: {
            headers: new AxiosHeaders({
              'x-phx-request-id': 'mock-request-id',
            }),
          },
        };
        return Promise.resolve(mockResponse);
      }
      const response = await baseService.post(
        '/checkout/paypal/upsell_cart',
        payload,
      );
      return response;
    } catch (error) {
      throw commonErrorformatter(error);
    }
  };
  checkoutApplyDiscountCode = async (payload: ApplyDiscountCodePayload) => {
    try {
      const response = await NewBaseService.post<CheckoutDataPayload>(
        '/checkout/apply_discount_code',
        payload,
      );
      return response;
    } catch (error) {
      throw commonErrorformatter(error as AxiosError);
    }
  };

  // getStoreDetails = async (shopId: string) => {
  //   try {
  //     const response = await baseService.get<Shop>(`/store/details/${shopId}`);
  //     return response;
  //   } catch (error) {
  //     throw commonErrorformatter(error as AxiosError);
  //   }
  // };

  getCheckout = async (payload: StartCheckoutPayload, uniqueId: string) => {
    const newPayload = {
      ...payload,
      ...getMetaPayload(uniqueId),
    };
    try {
      const response = await NewBaseService.post<PageDataResponse>(
        `/checkout/start_v2`,
        newPayload,
      );
      response.data.Page.pageData = decompressData(
        response?.data?.Page?.pageData,
      );
      return response;
    } catch (error) {
      console.error('Error in getCheckout funtion:', error);
      throw commonErrorformatter(error as AxiosError);
    }
  };
  subscribeToNewsLetter = async ({
    email,
    shopId,
  }: {
    email: string;
    shopId: string;
  }) => {
    try {
      const response = await NewBaseService.post('/checkout/newsletter', {
        email,
        shopId,
      });
      return response;
    } catch (error) {
      console.error('Error in subscribeToNewsLetter function:', error);
      throw commonErrorformatter(error as AxiosError);
    }
  };
  getCheckoutPageByStoreID = async (designStoreID: string) => {
    try {
      const response = await NewBaseService.post<GetPageData>(
        `/checkout/start_mock`,
        { ShopID: designStoreID },
      );
      return response;
    } catch (error) {
      console.error('Error in getCheckout funtion:', error);
      throw commonErrorformatter(error as AxiosError);
    }
  };

  sendCapiToServer = async (payload: { shopify_url: string }) => {
    try {
      await NewBaseService.post('/checkout/shopify/meta', payload, {
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (error) {
      throw commonErrorformatter(error as AxiosError);
    }
  };

  getPageById = async (pageId: string, smartUpsellFunnelID?: '') => {
    try {
      const response = await NewBaseService.get(
        `/checkout/page/${pageId}${smartUpsellFunnelID ? '?smartUpsellFunnelID=' + smartUpsellFunnelID : ''}`,
      );
      return response;
    } catch (error) {
      throw commonErrorformatter(error as AxiosError);
    }
  };

  getFunnelishPageById = async (funnelishId: string, shopId: string) => {
    try {
      const response = await NewBaseService.get(
        `/checkout/funnelish/${funnelishId}/${shopId}`,
      );
      response.data.Result.Page.pageData = decompressData(
        response?.data?.Result?.Page?.pageData,
      );
      return response;
    } catch (error) {
      throw commonErrorformatter(error as AxiosError);
    }
  };
  getCartDetails = async (
    funnelishId: string,
    shopId: string,
    cartId?: string,
  ) => {
    try {
      const response = await NewBaseService.get(
        `/checkout/funnelish/cart/${funnelishId}/${shopId}${cartId ? '/' + encodeURIComponent(cartId) : ''}`,
      );
      return response;
    } catch (error) {
      throw commonErrorformatter(error as AxiosError);
    }
  };

  getCountriesList = async (shopId: string) => {
    try {
      const response = await NewBaseService.get<ICountryResponse>(
        `checkout/get_countries/${shopId}`,
      );
      return response;
    } catch (error) {
      console.error('Error in getCountriesList funtion:', error);
      throw commonErrorformatter(error as AxiosError);
    }
  };
  setShippingMethod = async (payload: SetShippingMethodPayload) => {
    try {
      const response = await NewBaseService.post<SetOrderDetailsResponse>(
        '/checkout/set_shipping_method',
        payload,
      );
      return response;
    } catch (error) {
      throw commonErrorformatter(error as AxiosError);
    }
  };

  getUpsellAndOrderDetails = async (id: string) => {
    try {
      const response = await NewBaseService.get<{
        Result: { OrderDetails: OrderAndUpSellSummary[] };
      }>(`/checkout/get_order_summary/${id}`);
      return response;
    } catch (error) {
      throw commonErrorformatter(error as AxiosError);
    }
  };

  sendOrderDetails = async (payload: {
    orderId: string;
    mailId: string;
    storeCode: string;
  }) => {
    try {
      await NewBaseService.post('/checkout/club_shopify_orders', payload);
    } catch (error) {
      throw commonErrorformatter(error as AxiosError);
    }
  };

  getStoreDetails = async (
    shopId: string,
    eventToken?: string,
    isFunnelish = false,
  ) => {
    try {
      const urlEndPoint = eventToken
        ? `/checkout/store/details/${shopId}/?eventToken=${eventToken + (isFunnelish ? '&eventFrom=FUNNELISH' : '')}`
        : `/checkout/store/details/${shopId}`;
      const response = await NewBaseService.get<Shop>(urlEndPoint);
      return response;
    } catch (error) {
      throw commonErrorformatter(error);
    }
  };

  getDetailsStore = async (shopId: string, eventToken?: string) => {
    try {
      const urlEndPoint = eventToken
        ? `/checkout/details/store/${shopId}/?eventToken=${eventToken}`
        : `/checkout/details/store/${shopId}`;
      const response = await NewBaseService.get<Shop>(urlEndPoint);
      return response;
    } catch (error) {
      throw commonErrorformatter(error);
    }
  };

  getAllStoreProducts = async (
    storeId: number,
    baseService: AxiosInstance,
    payload: IQueryParams,
  ) => {
    try {
      const response = await baseService.get(
        `${getAllStoreProductsEP(storeId)}?${generateQueryParams(payload)}`,
      );
      return response;
    } catch (error) {
      return commonErrorformatter(error as AxiosError);
    }
  };

  getAllFunnelishConfig = async (
    storeId: number,
    baseService: AxiosInstance,
    pageID?: number,
  ) => {
    try {
      const response = await baseService.get(
        `/stores/getConfig/${storeId}${pageID ? '/' + pageID : ''}`,
      );
      return response;
    } catch (error) {
      return commonErrorformatter(error as AxiosError);
    }
  };

  createFunnelishConfig = async (
    baseService: AxiosInstance,
    payload: ICreateFunnelishConfig,
  ) => {
    try {
      const response = await baseService.post('/stores/createConfig', payload);
      return response;
    } catch (error) {
      return commonErrorformatter(error as AxiosError);
    }
  };

  saveFunnelishProducts = async (
    baseService: AxiosInstance,
    payload: ISaveFunnelishProducts,
  ) => {
    try {
      const response = await baseService.post(
        '/stores/checkout_funnelish_hosted/products/add_update',
        payload,
      );
      return response;
    } catch (error) {
      return commonErrorformatter(error as AxiosError);
    }
  };

  swapCartProduct = async (
    payload: ISwapCartProduct,
    eventFrom: string = 'Checkout Builder',
  ) => {
    try {
      const response = await NewBaseService.post(
        `/checkout/swap_cart_product?eventFrom=${encodeURIComponent(eventFrom)}`,
        payload,
      );
      return response;
    } catch (error) {
      return commonErrorformatter(error as AxiosError);
    }
  };

  getTemplateById = async (id: number, baseService: AxiosInstance) => {
    try {
      const response = await baseService.get(getTemplate(id));
      return response;
    } catch (error) {
      return commonErrorformatter(error as AxiosError);
    }
  };

  updateTemplate = async (
    payload: ICheckoutPage,
    baseService: AxiosInstance,
  ) => {
    try {
      const response = await baseService.put('/template', payload);
      return response;
    } catch (error) {
      return commonErrorformatter(error as AxiosError);
    }
  };

  fetchVisitorLocation = async () => {
    try {
      const response = await axios.get('https://ipapi.co/json/');
      return response.data;
    } catch (error) {
      console.error('Error fetching location:', error);
    }
  };

  getPreviewData = async (payload: { CartID: string; ShopID: string }) => {
    try {
      const response = await baseService.post(getPreviewPageEP, payload);
      return response;
    } catch (error) {
      throw commonErrorformatter(error as AxiosError);
    }
  };
  doMerchantValidation = async (payload: IDoMerchantValidation) => {
    try {
      const response = await NewBaseService.post(
        '/checkout/do_merchant_validation',
        payload,
      );
      return response;
    } catch (error) {
      throw commonErrorformatter(error);
    }
  };

  doVGSDecrypt = async (
    ID: string,
    payload: IDoVGSDecryptForGPay,
    // isSandbox: boolean,
  ) => {
    try {
      const response = await axios
        .create()
        .post(
          `https://${ID}${getIsStaging() ? '.sandbox' : '.live'}.verygoodproxy.com/post`,
          payload,
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 60000,
          },
        );
      return response.data;
    } catch (error) {
      throw commonErrorformatter(error);
    }
  };

  applePayLog = async (payload: IApplePayLog) => {
    try {
      const response = await NewBaseService.post(
        '/checkout/apple_pay_logs',
        payload,
      );
      return response;
    } catch (error) {
      throw commonErrorformatter(error);
    }
  };
}

const checkoutService = getIsViewIsEdit()
  ? new MockCheckoutService()
  : new CheckoutService();
export const useCheckoutService = new CheckoutService();
export default checkoutService;
